import React, { useEffect, useMemo, useState } from 'react';
import { Globe, Github, Linkedin, Twitter, Mail, MessageCircle, Instagram, Music, Sun, Moon } from 'lucide-react';

export default function SocialLinksHub() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem('theme') || 'dark';
  });
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  const socialLinks = useMemo(() => ([
    {
      name: 'Personal Website',
      url: 'https://athervasalunke18.vercel.app/',
      icon: Globe,
      description: 'My personal website'
    },
    {
      name: 'GitHub',
      url: 'https://github.com/AtWritesProg',
      icon: Github,
      description: 'Code & projects'
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/atherva-salunke-5ba508299/',
      icon: Linkedin,
      description: 'Professional network'
    },
    {
      name: 'X (Twitter)',
      url: 'https://x.com/ATsTweeting',
      icon: Twitter,
      description: 'Thoughts & updates'
    },
    {
      name: 'Email',
      url: 'mailto:athervasalunke1805@gmail.com',
      icon: Mail,
      description: 'Get in touch'
    },
    {
      name: 'Discord',
      url: 'https://discord.com/users/riot1759',
      icon: MessageCircle,
      description: 'Chat with me'
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/atherva_innit/',
      icon: Instagram,
      description: 'Photos & stories'
    },
    {
      name: 'Spotify',
      url: 'https://open.spotify.com/user/mz4s1llwdlcoebyeiz9hsmvgz',
      icon: Music,
      description: 'My music taste'
    }
  ]), []);

  const footerLinks = [
    { icon: Github, url: 'https://github.com/AtWritesProg' },
    { icon: Linkedin, url: 'https://linkedin.com/in/atherva-salunke-5ba508299' },
    { icon: Twitter, url: 'https://x.com/ATsTweeting' },
    { icon: Instagram, url: 'https://instagram.com/atherva_innit' },
    { icon: Music, url: 'https://open.spotify.com/user/mz4s1llwdlcoebyeiz9hsmvgz' },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-zinc-900 via-zinc-900 to-black dark:from-zinc-900 dark:via-zinc-950 dark:to-black">
      {/* Animated background accents */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl animate-[float_10s_ease-in-out_infinite]" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-fuchsia-500/10 blur-3xl animate-[float_12s_ease-in-out_infinite]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(75%_75%_at_50%_0%,rgba(124,58,237,0.15)_0%,rgba(0,0,0,0)_60%)]" />

      {/* Header with theme toggle */}
      <header className="relative z-10 w-full max-w-2xl mx-auto px-4 pt-6 flex items-center justify-between">
        <div className="text-xs tracking-widest uppercase text-zinc-400">links</div>
        <button
          aria-label="Toggle theme"
          onClick={toggleTheme}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-zinc-200 backdrop-blur transition hover:bg-white/15 dark:text-zinc-200"
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          {theme === 'dark' ? 'Light' : 'Dark'}
        </button>
      </header>

      {/* Hero/Profile */}
      <main className="relative z-10 w-full max-w-2xl mx-auto px-4 pb-16">
        <section className="pt-12 md:pt-16 text-center">
          <div className="mx-auto mb-6 h-24 w-24 rounded-full bg-gradient-to-br from-primary-600 to-fuchsia-600 p-[2px] shadow-[0_0_40px_rgba(139,92,246,0.35)]">
            {avatarError ? (
              <div className="h-full w-full rounded-full bg-zinc-900 grid place-items-center">
                <span className="text-2xl font-extrabold bg-gradient-to-br from-zinc-200 to-zinc-400 bg-clip-text text-transparent">AT</span>
              </div>
            ) : (
              <img src="/ATBoy.jpeg" alt="Atherva portrait" onError={() => setAvatarError(true)} className="h-full w-full rounded-full object-cover" />
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-100">Atherva Salunke</h1>
          <p className="mt-2 text-sm md:text-base text-zinc-400">Developer · Creator · Enthusiast</p>
        </section>

        {/* Links as glass cards */}
        <section className="mt-8 md:mt-10 grid gap-4">
          {socialLinks.map((link, index) => {
            const Icon = link.icon;
            return (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl transition-transform duration-200 hover:-translate-y-0.5 hover:bg-white/10 hover:shadow-[0_20px_60px_rgba(124,58,237,0.18)]"
              >
                {/* gradient border on hover */}
                <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-primary-500 via-fuchsia-400 to-transparent blur-[2px]" />
                </div>
                {/* subtle highlight sweep */}
                <div className="pointer-events-none absolute -top-1/2 left-0 h-full w-1/3 -skew-x-12 bg-gradient-to-r from-white/20 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />

                <div className="relative flex items-center gap-3">
                  {/* circular icon badge */}
                  <div className="grid place-items-center h-10 w-10 rounded-full border border-white/10 bg-white/10 backdrop-blur-md text-zinc-200 shadow-inner shadow-white/5 transition-colors group-hover:bg-white/20 group-hover:text-white">
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="text-zinc-100 font-semibold text-sm md:text-[15px]">
                      {link.name}
                    </div>
                    <div className="text-zinc-400 text-xs md:text-sm">
                      {link.description}
                    </div>
                  </div>
                  <div className="text-zinc-500 group-hover:text-zinc-300 transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </a>
            );
          })}
        </section>

        {/* Footer social bar */}
        <footer className="mt-10 flex items-center justify-center gap-4 text-zinc-500">
          {footerLinks.map(({ icon: Icon, url }, i) => (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur transition hover:bg-white/10 hover:text-white"
            >
              <Icon size={18} />
            </a>
          ))}
        </footer>
      </main>
    </div>
  );
}
